import React, { useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Moment from "react-moment";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getMachine, getServiceReports, PublicState } from "../../publicSlice";
import { ServiceReportStatusCode, serviceReportCode } from "../../models/serviceReportsModels";
import { dt } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";
import { localizeDateFormat } from "../../v2/shared/dateFormat";
import KioskBackButton from "../kiosk/KioskBackButton";

const KioskServiceReportsList: React.FC = () => {
  const { id, filter } = useParams<{ id: string; filter: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { translate, i18n } = useTranslation("public");
  const dateTimeFormat = localizeDateFormat("DD.MM.YYYY, HH:mm", i18n.language);

  const { machine, serviceReports, loading } = useAppSelector<PublicState>(
    (state: any) => state.public
  );

  const isOpen = filter === "open";

  useEffect(() => {
    if (id && !machine) {
      dispatch(getMachine(id));
    }
  }, [id, machine, dispatch]);

  useEffect(() => {
    if (machine?.internalNumber) {
      dispatch(
        getServiceReports({
          machineNumber: machine.internalNumber,
          query: { statusCodes: ["InProgress", "Solved"] },
        })
      );
    }
  }, [machine?.internalNumber, dispatch]);

  const filteredReports = useMemo(() => {
    if (!serviceReports) return [];
    const targetStatus = isOpen
      ? ServiceReportStatusCode.inProgress
      : ServiceReportStatusCode.solved;
    return serviceReports.filter((r) => r.statusCode === targetStatus);
  }, [serviceReports, isOpen]);

  const statusColor = isOpen ? "#F39C12" : "#6B7280";
  const StatusIcon = isOpen ? AssignmentOutlined : HistoryOutlined;

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        px: 2,
        pb: "80px",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          mb: 2,
        }}
      >
        <StatusIcon sx={{ fontSize: "24px", color: statusColor }} />
        <Box
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: dt.textPrimary,
          }}
        >
          {isOpen ? translate("kioskOpenServices") : translate("kioskServiceArchive")}
        </Box>
        {filteredReports.length > 0 && (
          <Box
            sx={{
              minWidth: "20px",
              padding: "0 8px",
              borderRadius: "10px",
              backgroundColor: statusColor,
              color: "#FFFFFF",
              fontSize: "12px",
              fontWeight: 700,
              fontFamily: dt.fontMono,
              textAlign: "center",
              lineHeight: "20px",
            }}
          >
            {filteredReports.length}
          </Box>
        )}
      </Box>

      {/* List */}
      {loading && !serviceReports ? (
        <Box sx={{ textAlign: "center", color: dt.textMuted, py: 4, fontSize: "14px" }}>
          {translate("loading")}
        </Box>
      ) : filteredReports.length === 0 ? (
        <Box
          sx={{
            backgroundColor: dt.cardBg,
            borderRadius: dt.radius,
            border: `1px solid ${dt.cardBorder}`,
            boxShadow: dt.cardShadow,
            padding: "24px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", color: dt.textSecondary }}>
            {isOpen ? translate("noOpenReports") : translate("noSolvedReports")}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {filteredReports.map((report, index) => (
            <Box
              key={report.number}
              onClick={() => navigate(`/public/service-reports/${report.number}`)}
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radius,
                border: `1px solid ${dt.cardBorder}`,
                borderLeft: `3px solid ${statusColor}`,
                boxShadow: dt.cardShadow,
                padding: "14px 16px",
                cursor: "pointer",
                userSelect: "none",
                WebkitTapHighlightColor: "transparent",
                transition: "transform 150ms ease",
                animation: `tileEnter 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
                "&:active": {
                  transform: "scale(0.98)",
                },
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Report number */}
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    fontFamily: dt.fontMono,
                    color: dt.textLink,
                    letterSpacing: "0.3px",
                  }}
                >
                  #{report.number}
                </Box>

                {/* Title */}
                <Box
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: dt.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mt: "2px",
                  }}
                >
                  {report.title}
                </Box>

                {/* Date */}
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    fontFamily: dt.fontMono,
                    color: dt.textMuted,
                    mt: "4px",
                  }}
                >
                  <Moment date={report.openDate} format={dateTimeFormat} />
                  {report.closeDate && (
                    <>
                      {" \u2014 "}
                      <Moment date={report.closeDate} format={dateTimeFormat} />
                    </>
                  )}
                </Box>
              </Box>

              <ChevronRightOutlined
                sx={{ fontSize: "20px", color: dt.textMuted, flexShrink: 0 }}
              />
            </Box>
          ))}
        </Box>
      )}

      <KioskBackButton />
    </Box>
  );
};

export default KioskServiceReportsList;
